import Session from '@/plugins/session'
import Device from '@/plugins/device'
import Resource from '@/plugins/sdk/Resource'
import chunk from 'lodash/chunk'
import toInteger from 'lodash/toInteger'
import last from 'lodash/last'
import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
import isSafeInteger from 'lodash/isSafeInteger'
import { singularize, pluralize } from 'inflected'
import urlJoin from 'url-join'

export const GET = 'GET'
export const HEAD = 'HEAD'
export const POST = 'POST'
export const PUT = 'PUT'
export const DELETE = 'DELETE'
export const CONNECT = 'CONNECT'
export const OPTIONS = 'OPTIONS'
export const TRACE = 'TRACE'
export const PATCH = 'PATCH'

export const REQUEST_TYPES = [
  GET,
  HEAD,
  POST,
  PUT,
  DELETE,
  CONNECT,
  OPTIONS,
  TRACE,
  PATCH
]

export default class {
  constructor () {
    Object.defineProperties(this, {
      _hostname: {
        configurable: false,
        enumerable: false,
        value: process.env.VUE_APP_API_HOSTNAME,
        writable: false
      },
      _basepath: {
        configurable: false,
        enumerable: false,
        value: '/api/v3/',
        writable: false
      },
      _pathname: {
        configurable: false,
        enumerable: false,
        value: '',
        writable: true
      },
      _parameters: {
        configurable: false,
        enumerable: false,
        value: new URLSearchParams(),
        writable: false
      },
      _headers: {
        configurable: false,
        enumerable: false,
        value: new Headers(),
        writable: false
      },
      _method: {
        configurable: false,
        enumerable: false,
        value: GET,
        writable: true
      },
      _body: {
        configurable: false,
        enumerable: false,
        value: '',
        writable: true
      }
    })
  }

  /**
   * Gets the URL.
   */
  get url () {
    let paths = this._pathname.split('/')

    // If we are posting to a resource and the last path segment
    // is not a UUID, we need to enforce that the resource of the
    // last path segment is a singular, and not a plural.
    if (this._method === POST && !(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(last(paths)))) {
      paths[paths.length - 1] = singularize(paths[paths.length - 1])
    }

    let parameters = this._parameters.toString()

    if (parameters.length) {
      parameters = `?${parameters}`
    }

    return new URL(
      urlJoin(
        this._hostname,
        this._basepath,
        paths.map(path => path.replace(/_/g, '')).join('/'),
        parameters
      )
    )
  }

  /**
   * Sets the request method.
   */
  set method (method) {
    if (REQUEST_TYPES.includes(method)) {
      this._method = method
    }
  }

  /**
   * Sets the headers.
   */
  set headers (headers) {
    if (isPlainObject(headers)) {
      Object
        .keys(headers)
        .map(h => this._headers.set(h, headers[h]))
    }
  }

  /**
   * Sets the body.
   */
  set body (body) {
    this._body = JSON.stringify(body)
  }

  /**
   * Sets the context.
   */
  set context (resource) {
    let identifier

    if (isArray(resource)) {
      resource.map((resource) => {
        this.context = resource
      })
    } else if (resource.prototype instanceof Resource) {
      resource = resource.name
    } else if (resource instanceof Resource) {
      identifier = resource.id
      resource = resource.constructor.name
    } else if (isPlainObject(resource)) {
      identifier = (resource.id || resource.identifier)
      resource = (resource.name || resource.resource)
    }

    if (isString(resource)) {
      const p = [ identifier ? singularize(resource) : pluralize(resource), identifier, ...this._pathname.split('/') ].filter(Boolean)
      p.map((t, a) => [ p.findIndex(p => p.startsWith(`${t}_`)), a ]).map(p => p[0] >= 0 ? p : void 0).filter(Boolean).map(t => { p[t[0]] = p[t[0]].substr(p[t[0]].indexOf(`${p[t[1]]}_`) + (p[t[1]].length + 1)) })
      this._pathname = p.map(path => path.startsWith('dictionary_') ? 'dictionaries/' + path.split('dictionary_').pop() : path).join('/')
    }
  }

  /**
   * Sets the filters.
   */
  set filters (filters) {
    if (isArray(filters)) {
      this._parameters.set('filters', filters.filter(filter => filter.isValid).join(' and '))
    } else if (isPlainObject(filters)) {
      // Previous filtering implementation
      Object
        .keys(filters)
        .map(f => this._parameters.set(f, filters[f]))
    }
  }

  /**
   * Sets the resource includes.
   */
  set include (includes) {
    if (isArray(includes)) {
      includes = includes.join(',')
    }

    if (isString(includes)) {
      this._parameters.set('include', includes)
    }
  }

  /**
   * Sets the result ordering.
   */
  set order (order) {
    if (isArray(order) && order.length >= 2) {
      order = chunk(order, 2)
        .filter(o => o.length === 2)
        .reduce((a, o) => o.join(':'), [])
        .join(',')
    }

    if (isString(order) && order.includes(':')) {
      this._parameters.set('order', order)
    }
  }

  /**
   * Sets the page number.
   */
  set page (page) {
    page = toInteger(page)

    if (isSafeInteger(page)) {
      this._parameters.set('page', page)
    }
  }

  /**
   * Sets the result limit.
   */
  set limit (limit) {
    limit = toInteger(limit)

    if (isSafeInteger(limit)) {
      this._parameters.set('limit', limit)
    }
  }

  /**
   * Builds a request instance.
   */
  async build () {
    if (!this._headers.has('Content-Type')) {
      this._headers.set('Content-Type', 'application/json')
    }

    if (this._pathname === 'file') {
      this._headers.delete('Content-Type')
    }

    if (await Session.active) {
      this._headers.set('Authorization', `Bearer ${await Session.token}`)
    }

    this._headers.set('X-OpenFreight-Fingerprint', await Device.fingerprint)

    return new Request(this.url, {
      method: this._method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: this._headers,
      redirect: 'follow',
      referrer: 'no-referrer',
      body: ([ GET, HEAD ].includes(this._method) ? undefined : this._body)
    })
  }
}
